import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../../components/ui/dropdown-menu';
import {
  Link2,
  Folder,
  FolderPlus,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Move,
  Globe,
  Lock,
  Youtube,
  FileText,
  Code,
  Palette,
  ExternalLink,
  Sparkles,
  Loader2,
  Tag,
  X,
  ChevronRight,
  Clock,
  User,
  FolderOpen,
  BookmarkPlus,
  LayoutGrid,
  List
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';

const getLinkTypeIcon = (type) => {
  switch (type) {
    case 'youtube': return <Youtube className="w-4 h-4 text-red-500" />;
    case 'code': return <Code className="w-4 h-4 text-green-500" />;
    case 'design': return <Palette className="w-4 h-4 text-purple-500" />;
    case 'document': return <FileText className="w-4 h-4 text-blue-500" />;
    default: return <Link2 className="w-4 h-4 text-slate-500" />;
  }
};

const SavedLinksPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  
  const [folders, setFolders] = useState([]);
  const [links, setLinks] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' ou 'cards'
  
  // Dialogs
  const [showAddLink, setShowAddLink] = useState(false);
  const [showAddFolder, setShowAddFolder] = useState(false);
  const [showEditLink, setShowEditLink] = useState(false);
  const [showEditFolder, setShowEditFolder] = useState(false);
  const [showMoveLink, setShowMoveLink] = useState(false);
  
  // Form states
  const [linkForm, setLinkForm] = useState({ url: '', title: '', description: '', folder_id: '', tags: [], is_public: false });
  const [folderForm, setFolderForm] = useState({ name: '', description: '', is_public: false });
  const [newTag, setNewTag] = useState('');
  const [selectedLink, setSelectedLink] = useState(null);
  const [selectedFolderEdit, setSelectedFolderEdit] = useState(null);
  
  // Loading states
  const [saving, setSaving] = useState(false);
  const [summarizing, setSummarizing] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [foldersRes, linksRes, tagsRes] = await Promise.all([
        api.get('/saved-links/folders'),
        api.get('/saved-links/links', { 
          params: { 
            folder_id: selectedFolder || undefined,
            search: searchQuery || undefined,
            tag: selectedTag || undefined
          }
        }),
        api.get('/saved-links/tags')
      ]);
      setFolders(foldersRes.data || []);
      setLinks(linksRes.data || []);
      setAllTags(tagsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedFolder, searchQuery, selectedTag]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== '') {
        fetchData();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchData]);

  // Add link
  const handleAddLink = async () => {
    if (!linkForm.url) {
      toast.error('L\'URL est requise');
      return;
    }
    
    setSaving(true);
    try {
      const res = await api.post('/saved-links/links', {
        ...linkForm,
        folder_id: linkForm.folder_id || null
      });
      setLinks(prev => [res.data, ...prev]);
      toast.success('Lien sauvegardé !');
      setShowAddLink(false);
      setLinkForm({ url: '', title: '', description: '', folder_id: '', tags: [], is_public: false });
      
      // Auto-summarize YouTube videos
      if (res.data.link_type === 'youtube') {
        handleSummarize(res.data.id);
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Update link
  const handleUpdateLink = async () => {
    if (!selectedLink) return;
    
    setSaving(true);
    try {
      await api.put(`/saved-links/links/${selectedLink.id}`, linkForm);
      await fetchData();
      toast.success('Lien mis à jour !');
      setShowEditLink(false);
      setSelectedLink(null);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  // Delete link
  const handleDeleteLink = async (linkId) => {
    if (!confirm('Supprimer ce lien ?')) return;
    
    try {
      await api.delete(`/saved-links/links/${linkId}`);
      setLinks(prev => prev.filter(l => l.id !== linkId));
      toast.success('Lien supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Move link
  const handleMoveLink = async (folderId) => {
    if (!selectedLink) return;
    
    try {
      await api.put(`/saved-links/links/${selectedLink.id}/move`, { folder_id: folderId || null });
      await fetchData();
      toast.success('Lien déplacé');
      setShowMoveLink(false);
      setSelectedLink(null);
    } catch (error) {
      toast.error('Erreur lors du déplacement');
    }
  };

  // Summarize link with AI
  const handleSummarize = async (linkId) => {
    setSummarizing(linkId);
    try {
      const res = await api.post(`/saved-links/links/${linkId}/summarize`);
      setLinks(prev => prev.map(l => l.id === linkId ? res.data : l));
      toast.success('Résumé généré !');
    } catch (error) {
      toast.error('Erreur lors de la génération du résumé');
    } finally {
      setSummarizing(null);
    }
  };

  // Add folder
  const handleAddFolder = async () => {
    if (!folderForm.name) {
      toast.error('Le nom est requis');
      return;
    }
    
    setSaving(true);
    try {
      const res = await api.post('/saved-links/folders', folderForm);
      setFolders(prev => [...prev, res.data]);
      toast.success('Dossier créé !');
      setShowAddFolder(false);
      setFolderForm({ name: '', description: '', is_public: false });
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  // Update folder
  const handleUpdateFolder = async () => {
    if (!selectedFolderEdit) return;
    
    setSaving(true);
    try {
      await api.put(`/saved-links/folders/${selectedFolderEdit.id}`, folderForm);
      await fetchData();
      toast.success('Dossier mis à jour !');
      setShowEditFolder(false);
      setSelectedFolderEdit(null);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  // Delete folder
  const handleDeleteFolder = async (folderId) => {
    if (!confirm('Supprimer ce dossier ? Les liens seront déplacés à la racine.')) return;
    
    try {
      await api.delete(`/saved-links/folders/${folderId}`);
      setFolders(prev => prev.filter(f => f.id !== folderId));
      if (selectedFolder === folderId) setSelectedFolder(null);
      await fetchData();
      toast.success('Dossier supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Add tag to link form
  const addTag = () => {
    if (newTag && !linkForm.tags.includes(newTag)) {
      setLinkForm(prev => ({ ...prev, tags: [...prev.tags, newTag] }));
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setLinkForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) + 
           ' ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const openEditLink = (link) => {
    setSelectedLink(link);
    setLinkForm({
      url: link.url,
      title: link.title,
      description: link.description || '',
      folder_id: link.folder_id || '',
      tags: link.tags || [],
      is_public: link.is_public
    });
    setShowEditLink(true);
  };

  const openEditFolder = (folder) => {
    setSelectedFolderEdit(folder);
    setFolderForm({
      name: folder.name,
      description: folder.description || '',
      is_public: folder.is_public
    });
    setShowEditFolder(true);
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
              Mes Liens & Ressources
            </h1>
            <p className="text-sm text-slate-500">Sauvegardez, organisez et résumez vos ressources avec l'IA</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowAddFolder(true)}>
              <FolderPlus className="w-4 h-4 mr-2" />
              Nouveau dossier
            </Button>
            <Button onClick={() => setShowAddLink(true)} style={{ backgroundColor: theme.colors?.accent }}>
              <BookmarkPlus className="w-4 h-4 mr-2" />
              Sauvegarder un lien
            </Button>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-4 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher par titre, description, tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          {selectedTag && (
            <Badge 
              variant="secondary" 
              className="flex items-center gap-1 cursor-pointer"
              onClick={() => setSelectedTag(null)}
            >
              <Tag className="w-3 h-3" />
              {selectedTag}
              <X className="w-3 h-3" />
            </Badge>
          )}
          {/* Toggle vue */}
          <div className="flex items-center border rounded-lg p-1 bg-white">
            <button
              onClick={() => setViewMode('table')}
              className={cn(
                "p-2 rounded transition-colors",
                viewMode === 'table' ? "bg-slate-100" : "hover:bg-slate-50"
              )}
              title="Vue tableau"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={cn(
                "p-2 rounded transition-colors",
                viewMode === 'cards' ? "bg-slate-100" : "hover:bg-slate-50"
              )}
              title="Vue cartes"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Sidebar - Folders */}
          <div className="w-64 hidden md:block space-y-2">
            <Card className="p-3">
              <div className="space-y-1">
                <button
                  onClick={() => setSelectedFolder(null)}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors",
                    !selectedFolder ? "bg-slate-100 font-medium" : "hover:bg-slate-50"
                  )}
                >
                  <Link2 className="w-4 h-4" />
                  <span>Tous les liens</span>
                  <span className="ml-auto text-xs text-slate-400">{links.length}</span>
                </button>
                
                <div className="pt-2 pb-1 px-3 text-xs font-medium text-slate-400 uppercase">
                  Dossiers
                </div>
                
                {folders.map(folder => (
                  <div key={folder.id} className="group relative">
                    <button
                      onClick={() => setSelectedFolder(folder.id)}
                      className={cn(
                        "w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors",
                        selectedFolder === folder.id ? "bg-slate-100 font-medium" : "hover:bg-slate-50"
                      )}
                    >
                      {folder.is_public ? (
                        <FolderOpen className="w-4 h-4 text-indigo-500" />
                      ) : (
                        <Folder className="w-4 h-4 text-slate-400" />
                      )}
                      <span className="truncate flex-1">{folder.name}</span>
                      {!folder.is_owner && (
                        <User className="w-3 h-3 text-slate-300" />
                      )}
                      <span className="text-xs text-slate-400">{folder.link_count}</span>
                    </button>
                    
                    {folder.is_owner && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-200 rounded">
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={() => openEditFolder(folder)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleDeleteFolder(folder.id)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                ))}
                
                {folders.length === 0 && (
                  <p className="text-sm text-slate-400 px-3 py-2">Aucun dossier</p>
                )}
              </div>
            </Card>

            {/* Tags */}
            {allTags.length > 0 && (
              <Card className="p-3">
                <div className="text-xs font-medium text-slate-400 uppercase mb-2 px-2">
                  Tags populaires
                </div>
                <div className="flex flex-wrap gap-1">
                  {allTags.slice(0, 15).map(tag => (
                    <Badge
                      key={tag.name}
                      variant="secondary"
                      className={cn(
                        "cursor-pointer text-xs",
                        selectedTag === tag.name && "bg-indigo-100 text-indigo-700"
                      )}
                      onClick={() => setSelectedTag(selectedTag === tag.name ? null : tag.name)}
                    >
                      {tag.name}
                    </Badge>
                  ))}
                </div>
              </Card>
            )}
          </div>

          {/* Main Content - Links */}
          <div className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : links.length === 0 ? (
              <Card className="p-12 text-center">
                <BookmarkPlus className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium mb-2" style={{ color: theme.colors?.primary }}>
                  Aucun lien sauvegardé
                </h3>
                <p className="text-slate-500 mb-4">
                  Commencez par sauvegarder un lien, une vidéo YouTube ou un tutoriel
                </p>
                <Button onClick={() => setShowAddLink(true)} style={{ backgroundColor: theme.colors?.accent }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Sauvegarder un lien
                </Button>
              </Card>
            ) : viewMode === 'table' ? (
              /* Vue Tableau Compacte */
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium text-slate-600">Titre</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Tags</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-600">Date</th>
                        <th className="text-center px-4 py-3 font-medium text-slate-600 w-20">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {links.map(link => (
                        <tr key={link.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 min-w-0">
                              {getLinkTypeIcon(link.link_type)}
                              <div className="min-w-0 flex-1">
                                <a 
                                  href={link.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="font-medium hover:underline truncate block"
                                  style={{ color: theme.colors?.primary }}
                                >
                                  {link.title}
                                </a>
                                <p className="text-xs text-slate-400 truncate">{link.url}</p>
                              </div>
                              {link.is_public ? (
                                <Globe className="w-3 h-3 text-green-500 flex-shrink-0" title="Public" />
                              ) : (
                                <Lock className="w-3 h-3 text-slate-400 flex-shrink-0" title="Privé" />
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                              {(link.tags || []).slice(0, 3).map(tag => (
                                <Badge 
                                  key={tag} 
                                  variant="secondary" 
                                  className="text-xs cursor-pointer"
                                  onClick={() => setSelectedTag(tag)}
                                >
                                  {tag}
                                </Badge>
                              ))}
                              {(link.tags?.length || 0) > 3 && (
                                <Badge variant="outline" className="text-xs">+{link.tags.length - 3}</Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-xs text-slate-500">{formatDateTime(link.created_at)}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <a href={link.url} target="_blank" rel="noopener noreferrer">
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <ExternalLink className="w-4 h-4" />
                                </Button>
                              </a>
                              {link.is_owner && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                      <MoreVertical className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => openEditLink(link)}>
                                      <Edit className="w-4 h-4 mr-2" />
                                      Modifier
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => { setSelectedLink(link); setShowMoveLink(true); }}>
                                      <Move className="w-4 h-4 mr-2" />
                                      Déplacer
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleSummarize(link.id)}>
                                      <Sparkles className="w-4 h-4 mr-2" />
                                      Résumer (IA)
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleDeleteLink(link.id)} className="text-red-600">
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Supprimer
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            ) : (
              /* Vue Cartes */
              <div className="grid gap-4">
                {links.map(link => (
                  <Card key={link.id} className="overflow-hidden hover:shadow-md transition-shadow">
                    <div className="flex">
                      {/* Thumbnail */}
                      {link.thumbnail_url ? (
                        <div className="w-40 h-28 flex-shrink-0 bg-slate-100">
                          <img 
                            src={link.thumbnail_url} 
                            alt="" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-20 h-28 flex-shrink-0 bg-slate-100 flex items-center justify-center">
                          {getLinkTypeIcon(link.link_type)}
                        </div>
                      )}
                      
                      {/* Content */}
                      <div className="flex-1 p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {getLinkTypeIcon(link.link_type)}
                              <a 
                                href={link.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="font-medium hover:underline truncate"
                                style={{ color: theme.colors?.primary }}
                              >
                                {link.title}
                              </a>
                              {link.is_public ? (
                                <Globe className="w-3 h-3 text-green-500" title="Public" />
                              ) : (
                                <Lock className="w-3 h-3 text-slate-400" title="Privé" />
                              )}
                            </div>
                            
                            <p className="text-xs text-slate-400 truncate mb-2">{link.url}</p>
                            
                            {/* AI Summary */}
                            {link.ai_summary && (
                              <div className="mb-2 p-2 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-100">
                                <div className="flex items-center gap-1 text-xs text-indigo-600 mb-1">
                                  <Sparkles className="w-3 h-3" />
                                  Résumé IA
                                </div>
                                <p className="text-sm text-slate-700">{link.ai_summary}</p>
                              </div>
                            )}
                            
                            {link.description && !link.ai_summary && (
                              <p className="text-sm text-slate-600 line-clamp-2 mb-2">{link.description}</p>
                            )}
                            
                            {/* Tags */}
                            <div className="flex flex-wrap gap-1">
                              {(link.ai_keywords || []).map(kw => (
                                <Badge 
                                  key={kw} 
                                  variant="outline" 
                                  className="text-xs bg-purple-50 border-purple-200 text-purple-700 cursor-pointer"
                                  onClick={() => setSelectedTag(kw)}
                                >
                                  {kw}
                                </Badge>
                              ))}
                              {(link.tags || []).map(tag => (
                                <Badge 
                                  key={tag} 
                                  variant="secondary" 
                                  className="text-xs cursor-pointer"
                                  onClick={() => setSelectedTag(tag)}
                                >
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-1">
                            {!link.ai_summary && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSummarize(link.id)}
                                disabled={summarizing === link.id}
                                title="Générer un résumé IA"
                              >
                                {summarizing === link.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Sparkles className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            
                            <a href={link.url} target="_blank" rel="noopener noreferrer">
                              <Button variant="ghost" size="sm">
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </a>
                            
                            {link.is_owner && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => openEditLink(link)}>
                                    <Edit className="w-4 h-4 mr-2" />
                                    Modifier
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => { setSelectedLink(link); setShowMoveLink(true); }}>
                                    <Move className="w-4 h-4 mr-2" />
                                    Déplacer
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleSummarize(link.id)}>
                                    <Sparkles className="w-4 h-4 mr-2" />
                                    Régénérer le résumé
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => handleDeleteLink(link.id)} className="text-red-600">
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Supprimer
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </div>
                        
                        {/* Meta */}
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(link.created_at)}
                          </span>
                          {!link.is_owner && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {link.owner_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Link Dialog */}
      <Dialog open={showAddLink} onOpenChange={setShowAddLink}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookmarkPlus className="w-5 h-5" style={{ color: theme.colors?.accent }} />
              Sauvegarder un lien
            </DialogTitle>
            <DialogDescription>
              Ajoutez une URL. Les vidéos YouTube seront automatiquement résumées par l'IA.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>URL *</Label>
              <Input
                value={linkForm.url}
                onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })}
                placeholder="https://..."
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Titre (optionnel)</Label>
              <Input
                value={linkForm.title}
                onChange={(e) => setLinkForm({ ...linkForm, title: e.target.value })}
                placeholder="Titre du lien"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Description (optionnel)</Label>
              <Textarea
                value={linkForm.description}
                onChange={(e) => setLinkForm({ ...linkForm, description: e.target.value })}
                placeholder="Notes personnelles..."
                rows={2}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Dossier</Label>
              <Select 
                value={linkForm.folder_id || "none"} 
                onValueChange={(v) => setLinkForm({ ...linkForm, folder_id: v === "none" ? "" : v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Aucun (racine)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun (racine)</SelectItem>
                  {folders.filter(f => f.is_owner).map(folder => (
                    <SelectItem key={folder.id} value={folder.id}>
                      {folder.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Tags</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Ajouter un tag..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button type="button" variant="outline" onClick={addTag}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {linkForm.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {linkForm.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                      {tag}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => removeTag(tag)} />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-sm font-medium">Partager avec l'équipe</Label>
                <p className="text-xs text-slate-500">Rendre ce lien visible par tous les membres</p>
              </div>
              <Switch
                checked={linkForm.is_public}
                onCheckedChange={(checked) => setLinkForm({ ...linkForm, is_public: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLink(false)}>Annuler</Button>
            <Button onClick={handleAddLink} disabled={saving} style={{ backgroundColor: theme.colors?.accent }}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Link Dialog */}
      <Dialog open={showEditLink} onOpenChange={setShowEditLink}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifier le lien</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Titre</Label>
              <Input
                value={linkForm.title}
                onChange={(e) => setLinkForm({ ...linkForm, title: e.target.value })}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={linkForm.description}
                onChange={(e) => setLinkForm({ ...linkForm, description: e.target.value })}
                rows={2}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Dossier</Label>
              <Select 
                value={linkForm.folder_id || "none"} 
                onValueChange={(v) => setLinkForm({ ...linkForm, folder_id: v === "none" ? "" : v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Aucun (racine)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun (racine)</SelectItem>
                  {folders.filter(f => f.is_owner).map(folder => (
                    <SelectItem key={folder.id} value={folder.id}>
                      {folder.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Tags</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="Ajouter un tag..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button type="button" variant="outline" onClick={addTag}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {linkForm.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {linkForm.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                      {tag}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => removeTag(tag)} />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-sm font-medium">Partager avec l'équipe</Label>
                <p className="text-xs text-slate-500">Rendre ce lien visible par tous</p>
              </div>
              <Switch
                checked={linkForm.is_public}
                onCheckedChange={(checked) => setLinkForm({ ...linkForm, is_public: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditLink(false)}>Annuler</Button>
            <Button onClick={handleUpdateLink} disabled={saving} style={{ backgroundColor: theme.colors?.accent }}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Link Dialog */}
      <Dialog open={showMoveLink} onOpenChange={setShowMoveLink}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Déplacer le lien</DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-2">
            <button
              onClick={() => handleMoveLink(null)}
              className="w-full flex items-center gap-2 p-3 rounded-lg hover:bg-slate-100 transition-colors text-left"
            >
              <Link2 className="w-4 h-4" />
              Racine (aucun dossier)
            </button>
            {folders.filter(f => f.is_owner).map(folder => (
              <button
                key={folder.id}
                onClick={() => handleMoveLink(folder.id)}
                className={cn(
                  "w-full flex items-center gap-2 p-3 rounded-lg hover:bg-slate-100 transition-colors text-left",
                  selectedLink?.folder_id === folder.id && "bg-slate-100"
                )}
              >
                <Folder className="w-4 h-4" />
                {folder.name}
                {selectedLink?.folder_id === folder.id && (
                  <Badge variant="secondary" className="ml-auto text-xs">Actuel</Badge>
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Folder Dialog */}
      <Dialog open={showAddFolder} onOpenChange={setShowAddFolder}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderPlus className="w-5 h-5" style={{ color: theme.colors?.accent }} />
              Nouveau dossier
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nom du dossier *</Label>
              <Input
                value={folderForm.name}
                onChange={(e) => setFolderForm({ ...folderForm, name: e.target.value })}
                placeholder="Ex: Tutoriels React, Design inspiration..."
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Description (optionnel)</Label>
              <Textarea
                value={folderForm.description}
                onChange={(e) => setFolderForm({ ...folderForm, description: e.target.value })}
                placeholder="Description du dossier..."
                rows={2}
                className="mt-1"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-sm font-medium">Dossier public</Label>
                <p className="text-xs text-slate-500">Visible par tous les membres de l'équipe</p>
              </div>
              <Switch
                checked={folderForm.is_public}
                onCheckedChange={(checked) => setFolderForm({ ...folderForm, is_public: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddFolder(false)}>Annuler</Button>
            <Button onClick={handleAddFolder} disabled={saving} style={{ backgroundColor: theme.colors?.accent }}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Folder Dialog */}
      <Dialog open={showEditFolder} onOpenChange={setShowEditFolder}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier le dossier</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nom du dossier</Label>
              <Input
                value={folderForm.name}
                onChange={(e) => setFolderForm({ ...folderForm, name: e.target.value })}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={folderForm.description}
                onChange={(e) => setFolderForm({ ...folderForm, description: e.target.value })}
                rows={2}
                className="mt-1"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-sm font-medium">Dossier public</Label>
                <p className="text-xs text-slate-500">Visible par tous les membres</p>
              </div>
              <Switch
                checked={folderForm.is_public}
                onCheckedChange={(checked) => setFolderForm({ ...folderForm, is_public: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditFolder(false)}>Annuler</Button>
            <Button onClick={handleUpdateFolder} disabled={saving} style={{ backgroundColor: theme.colors?.accent }}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default SavedLinksPage;
